import { setDay, isToday, isSameDay, parseISO, parse } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import _findIndex from 'lodash/findIndex';
import _isPlainObject from 'lodash/isPlainObject';

/**
 * @param {Date} date
 * @returns {String}
 */
export function dateObjectToNaiveISOString(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

export const isMessage = (obj = {}) => {
  if (!_isPlainObject(obj)) return false;
  return !!obj.id && !!obj.defaultMessage;
};

const DAYS_OF_WEEK = [
  { value: 'Su', dateTime: setDay(new Date(), 0) },
  { value: 'Mo', dateTime: setDay(new Date(), 1) },
  { value: 'Tu', dateTime: setDay(new Date(), 2) },
  { value: 'We', dateTime: setDay(new Date(), 3) },
  { value: 'Th', dateTime: setDay(new Date(), 4) },
  { value: 'Fr', dateTime: setDay(new Date(), 5) },
  { value: 'Sa', dateTime: setDay(new Date(), 6) },
];

// locales differ on the first day of the week (sat, sun, or mon)
export function getOffsetDaysOfWeek(startWeekOn, locale, weekday = 'short') {
  const doubleDOW = DAYS_OF_WEEK.concat(DAYS_OF_WEEK);
  const startIdx = _findIndex(doubleDOW, ({ value }) => startWeekOn.startsWith(value.toLowerCase()));
  const dow = doubleDOW.slice(startIdx, startIdx + DAYS_OF_WEEK.length);
  return !locale
    ? dow
    : dow.map(({ dateTime, value }) => ({
        dateTime,
        value: Intl.DateTimeFormat(locale, { weekday }).format(dateTime),
        original: value,
      }));
}

export function isTodayTimezone(date, timeZone) {
  if (!timeZone) {
    return isToday(date);
  }

  const now = new Date();
  const nowInTimezone = utcToZonedTime(now, timeZone);

  return isSameDay(date, nowInTimezone);
}

export function parseISOWithTimezone(isoDate, timeZone) {
  if (timeZone) {
    return utcToZonedTime(isoDate, timeZone);
  }
  return parseISO(isoDate);
}

/*
  Date objects are always coerced into the local timezone. In GMT-5 / EST / America/New_York...

    parseISO('2020-01-01T00:00:00Z').toString() // Tue Dec 31 2019 19:00:00 GMT-0500

  If we want that date to format as midnight, we need to adjust the time/date to anticipate the coersion...

    parseISO('2020-01-01T05:00:00Z').toString() // Wed Jan 1 2020 00:00:00 GMT-0500

  This function accepts a `date`, finds the corresponding date/time in the `timeZone`, and returns a date object
  in the original (local) timezone but with the date/time from the `timeZone`.

    const date = parseISO('2020-01-01T00:00:00Z') // Tue Dec 31 2019 19:00:00 GMT-0500
    const timeZone = 'Australia/Brisbane' // Wed Jan 1 2020 10:00:00 GMT+10
    const adjusted = adjustDateForTimezone(date, timeZone) // Wed Jan 1 2020 10:00:00 GMT-0500

  Now `adjusted` can be manipulated and will behave the way we expect.

    format(date, 'yyyy-MM-dd HH:mm:ss') // 2019-12-31 19:00:00
    format(adjusted, 'yyyy-MM-dd HH:mm:ss') // 2020-01-01 10:00:00
*/
const adjustDateForTimezone = utcToZonedTime;

/*
  When you need to create a date in a specific timezone and there's no timezone info in the date string.

    const dateString = '2020-01-01 00:00:00';
    parseISO(dateString).toString() // Wed Jan 1 2020 00:00:00 GMT-0500
    parseDateInTimezone(dateString, 'Australia/Brisbane').toString() // Tue Dec 31 2019 09:00:00 GMT-0500

*/
const parseDateInTimezone = zonedTimeToUtc;

export const format12HourTimeLowercase = (date, locale) => {
  const formattedTime = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  })
    .formatToParts(date)
    .map(({ type, value }) => {
      switch (type) {
        case 'dayPeriod':
          return value.toLowerCase();
        default:
          return value;
      }
    })
    .join('');

  return formattedTime;
};

export { adjustDateForTimezone, parseDateInTimezone, zonedTimeToUtc };

/**
 * Calculates the time remaining from now until a given deadline datetime
 * @param {string|Date} deadline - The deadline datetime (ISO string or Date object)
 * @returns {{hours: number, minutes: number}|null} - Object with hours and minutes, or null if deadline is invalid
 */
export function getTimeRemaining(deadline) {
  if (!deadline) return null;

  try {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffMs = deadlineDate.getTime() - now.getTime();

    if (diffMs <= 0) return { hours: 0, minutes: 0 };

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    return { hours, minutes };
  } catch (error) {
    return null;
  }
}

function coerceToNaiveDateString(dateString) {
  const [naiveDateString] = dateString.split('T');

  return naiveDateString;
}

export function naiveDateStringToDateObject(dateString) {
  if (!dateString) return null;

  const coerced = coerceToNaiveDateString(dateString);
  const formatString = 'yyyy-MM-dd';

  const asDateObject = new Date(dateString);
  const parsed = parse(coerced, formatString, asDateObject);

  if (Number.isNaN(parsed.getTime())) {
    reportError('Error parsing date string at helpers/intl-fns@naiveDateStringToDateObject', {
      dateString,
      coerced,
      asDateObject,
      parsed,
    });
  }

  return parsed;
}

// Map locales from react/src/helpers/supported-locales.js to https://github.com/date-fns/date-fns/tree/master/src/locale
const APP_LOCALE_TO_DATE_FNS_LOCALE_MAP = {
  'en-US': 'en-US',
  es: 'es',
  'es-MX': 'es',
  'fr-FR': 'fr',
  da: 'da',
  'de-DE': 'de',
  ja: 'ja',
  nb: 'nb',
  'nl-NL': 'nl',
  pt: 'pt',
  'pt-BR': 'pt-BR',
  sv: 'sv',
  'zh-TW': 'zh-TW',
};

// TODO(kamil): Use react/src/react-main.js DEFAULT_LOCALE as a fallback
export const getDateFnsLocaleFromAppLocale = locale =>
  APP_LOCALE_TO_DATE_FNS_LOCALE_MAP[locale] || APP_LOCALE_TO_DATE_FNS_LOCALE_MAP['en-US'];

export const protoTimeOfDayToDate = ({ hours = 0, minutes = 0, seconds = 0, nanos = 0 }) => {
  const date = new Date();
  date.setHours(hours, minutes, seconds, Math.floor(nanos / 1e6));
  return date;
};

export const formatProtoTimeOfDay = (intl, timeOfDay) => {
  if (!timeOfDay) return null;
  return intl.formatTime(protoTimeOfDayToDate(timeOfDay), {
    hour: 'numeric',
    minute: '2-digit',
  });
};

/**
 * Formats an ISO datetime string into separate time and date strings suitable
 * for use as i18n message values (e.g. email subject lines).
 *
 * Example output: { time: "10:00 AM", date: "Thursday, Apr 16, 2026" }
 *
 * @param {string} isoDateTime - ISO 8601 datetime string (e.g. "2026-04-16T16:00:00Z")
 * @param {string|undefined} timeZone - IANA timezone identifier (e.g. "America/New_York")
 * @param {object} intl - react-intl IntlShape object
 * @returns {{ time: string, date: string }}
 */
export const formatEmailSubjectDateTime = (isoDateTime, timeZone, intl) => ({
  time: intl.formatTime(isoDateTime, { hour: 'numeric', hour12: true, minute: '2-digit', timeZone }),
  date: intl.formatDate(isoDateTime, {
    day: 'numeric',
    month: 'short',
    timeZone,
    weekday: 'long',
    year: 'numeric',
  }),
});
